import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { formatApiError } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound, Send } from "lucide-react";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startCooldown = (s) => {
    setCooldown(s);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password/init", { email: email.trim().toLowerCase() });
      toast.success(data?.message || "If that email is registered, a code has been sent.");
      setStep(2);
      startCooldown(60);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send code");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password/resend", { email: email.trim().toLowerCase() });
      toast.success("A fresh code has been sent (if the account exists).");
      startCooldown(60);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resend");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (!otp.trim()) return toast.error("Enter the verification code");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password/verify", {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        new_password: pw,
      });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="mx-auto mb-4 inline-flex items-center gap-3 px-4 py-2 rounded-2xl bg-white/95 border border-zinc-800 shadow-md">
            <img src="/logo.png" alt="Sonotek Digital" className="h-10 w-auto" />
          </div>
          <div className="text-sm text-zinc-500 mt-2">Sonotek<span className="gradient-text font-semibold">Digi</span></div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight">Reset your password</h1>
          <p className="mt-2 text-sm text-zinc-500">
            {step === 1
              ? "We'll email a single-use code to your registered address."
              : `Enter the code we sent to ${email} and choose a new password.`}
          </p>
        </div>

        {step === 1 && (
          <form onSubmit={requestOtp} className="space-y-5 p-8 rounded-2xl border border-zinc-800 bg-zinc-950/60">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
              <Input required type="email" data-testid="forgot-email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-zinc-900 border-zinc-800 focus-visible:ring-cyan-500/50" />
            </div>
            <Button type="submit" disabled={busy} data-testid="forgot-send-otp"
              className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold hover:brightness-110">
              <Send className="w-4 h-4 mr-2" />{busy ? "Sending..." : "Send verification code"}
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verify} className="space-y-5 p-8 rounded-2xl border border-zinc-800 bg-zinc-950/60">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">6-digit code</Label>
              <Input
                required data-testid="forgot-otp" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
                className="mt-2 bg-zinc-900 border-zinc-800 tracking-[0.4em] font-mono text-lg"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">New Password</Label>
              <Input required type="password" minLength={6} data-testid="forgot-password" value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-2 bg-zinc-900 border-zinc-800 focus-visible:ring-cyan-500/50" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Confirm Password</Label>
              <Input required type="password" minLength={6} data-testid="forgot-password-confirm" value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="mt-2 bg-zinc-900 border-zinc-800 focus-visible:ring-cyan-500/50" />
            </div>
            <div className="flex items-center justify-between text-xs">
              <button
                type="button" onClick={resend} disabled={cooldown > 0 || busy}
                data-testid="forgot-resend"
                className="text-cyan-400 hover:underline disabled:text-zinc-600 disabled:no-underline">
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
              </button>
              <span className="text-zinc-500">Code expires in 10 minutes</span>
            </div>
            <Button type="submit" disabled={busy} data-testid="forgot-submit"
              className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold hover:brightness-110">
              <KeyRound className="w-4 h-4 mr-2" />{busy ? "Updating..." : "Reset password"}
            </Button>
          </form>
        )}

        <p className="text-center mt-6 text-sm text-zinc-500">
          Back to <Link to="/login" className="text-cyan-400 hover:underline">login</Link>
        </p>
      </div>
    </div>
  );
}
